import type { StorefrontProduct } from '../../api/client';
import type { ProductFacts } from './storeFacts';

// What a rail decides about one garment, kept apart from what it draws: which of a product's pictures is its front,
// back and side, and which single tag it wears.

/** The views of one garment; an empty one is a view the product has no picture of. */
export type RailFaces = { front: string; back: string; side: string };

/** How few units left make a garment worth saying so. */
const FEW_LEFT = 5;

/** The words in a picture's description that say which view it is, in the languages a store is served in. A merchant's other pictures are worn shots and details, so position says nothing. */
const SAYS_BACK = /(^|[^\p{L}])(back|dos|arrière|arriere|(?:ال)?خلف)($|[^\p{L}])/iu;
const SAYS_SIDE = /(^|[^\p{L}])(side|profile|profil|côté|cote|(?:ال)?جانب)($|[^\p{L}])/iu;

// Lists a product's own views: its pictures in order, leaving out any that belongs to one of its other options.
function ownViews(product: Pick<StorefrontProduct, 'images' | 'variants'>): StorefrontProduct['images'] {
  const tied = new Set(product.variants.map((variant) => variant.imageId).filter(Boolean));
  return product.images.filter((picture, at) => at === 0 || !tied.has(picture.id));
}

// Reads a garment's views from what each picture's description says; an option with a picture of its own shows that alone, since the product's back and side are of another colour.
export function railFaces(product: Pick<StorefrontProduct, 'images' | 'variants'>, chosenImageId: string | null | undefined): RailFaces {
  const own = ownViews(product);
  const chosen = chosenImageId ? product.images.find((picture) => picture.id === chosenImageId) : undefined;
  if (chosen && chosen.id !== own[0]?.id) return { front: chosen.src, back: '', side: '' };
  const said = (words: RegExp) => own.find((picture) => words.test(picture.altText ?? ''))?.src ?? '';
  return { front: own[0]?.src ?? '', back: said(SAYS_BACK), side: said(SAYS_SIDE) };
}

/** The one tag a garment wears: what kind it is, which paints it, and its words. */
export type RailTag = { kind: 'sale' | 'out' | 'low' | 'new'; words: string };

/** The words a tag may say, in the store's language. */
export type RailTagWords = { soldOut: string; isNew: string; onlyLeft: (count: number) => string };

// Picks the one tag a garment wears: out of stock first, since nothing else then matters, then its discount, how few are left, or that it is new.
export function railTag(facts: Pick<ProductFacts, 'discountPercent' | 'stock' | 'isNew'>, words: RailTagWords): RailTag | null {
  if (facts.stock === 0) return { kind: 'out', words: words.soldOut };
  if (facts.discountPercent) return { kind: 'sale', words: `-${facts.discountPercent}%` };
  if (typeof facts.stock === 'number' && facts.stock <= FEW_LEFT) return { kind: 'low', words: words.onlyLeft(facts.stock) };
  return facts.isNew ? { kind: 'new', words: words.isNew } : null;
}

// Names the newest garments on a rail, the only ones whose tag may say so: a new store's whole first month is new otherwise, and a tag on every hanger says nothing.
export function newestOnRail(rows: ReadonlyArray<Pick<StorefrontProduct, 'id' | 'createdAt'>>, count = 2): ReadonlySet<string> {
  const dated = rows.filter((row) => row.createdAt).sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''));
  return new Set(dated.slice(0, count).map((row) => row.id));
}

// Writes a rail's position as the two digits a label prints: "04 / 10".
export const railCount = (at: number, count: number): string => `${String(at + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
